// src/components/admin/settings/DonationSettings.jsx
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DollarSign } from 'lucide-react';
import styles from '../../../styles/Settings.module.css';
import useTranslation from '../../../hooks/useTranslation';
import { updateDonationSettings, selectDonationSettings } from '../../../store/slices/platformSettingsSlice';
import toast from 'react-hot-toast';
const DonationSettings = () => {
  const dispatch = useDispatch();
  const donationSettingsRedux = useSelector(selectDonationSettings);
  
  const { translatedText: donationSettings } = useTranslation('Donation Settings');
  const { translatedText: minDonationAmount } = useTranslation('Minimum Donation Amount');
  const { translatedText: maxDonationAmount } = useTranslation('Maximum Donation Amount');
  const { translatedText: defaultCurrency } = useTranslation('Default Currency');
  const { translatedText: platformFee } = useTranslation('Platform Fee (%)');
  const { translatedText: thankYouMessage } = useTranslation('Thank You Message');
  const { translatedText: allowedCurrencies } = useTranslation('Allowed Currencies');
  const { translatedText: saveChanges } = useTranslation('Save Changes');
  const { translatedText: savingText } = useTranslation('Saving...');

  const [localSettings, setLocalSettings] = React.useState(
    donationSettingsRedux || {
      minDonationAmount: 1,
      maxDonationAmount: 10000,
      defaultCurrency: 'USD',
      allowedCurrencies: ['USD', 'EUR', 'HTG'],
      platformFeePercentage: 0,
      thankYouMessage: 'Thank you for your generous donation!'
    }
  );

  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    if (donationSettingsRedux) {
      setLocalSettings(donationSettingsRedux);
    }
  }, [donationSettingsRedux]);

  const handleChange = (field, value) => {
    setLocalSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await dispatch(updateDonationSettings(localSettings)).unwrap();
      toast.success('Donation settings saved!');
    } catch (error) {
      toast.error(error || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={styles.settingsCard}>
      <h3>
        <DollarSign size={20} />
        {donationSettings || 'Donation Settings'}
      </h3>
      <div className={styles.settingsForm}>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label>{minDonationAmount || 'Minimum Donation Amount'}</label>
            <input
              type="number"
              value={localSettings.minDonationAmount}
              onChange={(e) => handleChange('minDonationAmount', Number(e.target.value))}
              min={0}
            />
          </div>
          <div className={styles.formGroup}>
            <label>{maxDonationAmount || 'Maximum Donation Amount'}</label>
            <input
              type="number"
              value={localSettings.maxDonationAmount}
              onChange={(e) => handleChange('maxDonationAmount', Number(e.target.value))}
              min={0}
            />
          </div>
        </div>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label>{defaultCurrency || 'Default Currency'}</label>
            <select
              value={localSettings.defaultCurrency}
              onChange={(e) => handleChange('defaultCurrency', e.target.value)}
            >
              <option value="USD">USD - US Dollar</option>
              <option value="EUR">EUR - Euro</option>
              <option value="GBP">GBP - British Pound</option>
              <option value="HTG">HTG - Haitian Gourde</option>
              <option value="CAD">CAD - Canadian Dollar</option>
            </select>
          </div>
          <div className={styles.formGroup}>
            <label>{platformFee || 'Platform Fee (%)'}</label>
            <input
              type="number"
              value={localSettings.platformFeePercentage}
              onChange={(e) => handleChange('platformFeePercentage', Number(e.target.value))}
              min={0}
              max={100}
              step={0.5}
            />
          </div>
        </div>
        <div className={styles.formGroup}>
          <label>{thankYouMessage || 'Thank You Message'}</label>
          <textarea
            value={localSettings.thankYouMessage}
            onChange={(e) => handleChange('thankYouMessage', e.target.value)}
            rows={3}
            placeholder="Custom thank you message for donors"
          />
        </div>
        <div className={styles.formGroup}>
          <label>{allowedCurrencies || 'Allowed Currencies'}</label>
          <div className={styles.currencyCheckboxes}>
            {['USD', 'EUR', 'GBP', 'HTG', 'CAD'].map(currency => (
              <label key={currency} className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={localSettings.allowedCurrencies?.includes(currency)}
                  onChange={(e) => {
                    const current = localSettings.allowedCurrencies || [];
                    let updated;
                    if (e.target.checked) {
                      updated = [...current, currency];
                    } else {
                      updated = current.filter(c => c !== currency);
                    }
                    handleChange('allowedCurrencies', updated);
                  }}
                />
                {currency}
              </label>
            ))}
          </div>
        </div>
        <button 
          className={styles.saveBtn}
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? (savingText || 'Saving...') : (saveChanges || 'Save Changes')}
        </button>
      </div>
    </div>
  );
};

export default DonationSettings;